/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Generic Tabs Hook
 *
 * Manages tab routing, visited tabs tracking, and URL synchronization.
 */

"use client";

import { useSearchParams } from "next/navigation";
import { useNavigationWithProgress } from "@/hooks/utilities/use-navigation-with-progress";
import { useEffect, useState } from "react";

export interface UseTabsOptions {
  /**
   * Allowed tab values
   * @default ["overview", "contacts", "jobsites", "projects"]
   */
  allowedTabs?: string[];

  /**
   * Default tab if none specified in URL
   * @default "overview"
   */
  defaultTab?: string;

  /**
   * Whether to sync active tab with URL search params
   * @default true
   */
  syncWithUrl?: boolean;
}

export interface UseTabsReturn {
  /**
   * Currently active tab
   */
  activeTab: string;

  /**
   * Set active tab (also updates URL if syncWithUrl is true)
   */
  setActiveTab: (tab: string) => void;

  /**
   * List of tabs that have been visited
   */
  visitedTabs: string[];

  /**
   * Check if a tab has been visited
   */
  isTabVisited: (tab: string) => boolean;

  /**
   * Mark a tab as visited
   */
  markTabVisited: (tab: string) => void;

  /**
   * Reset visited tabs
   */
  resetVisitedTabs: () => void;
}

/**
 * Generic hook for managing tabs with URL synchronization
 *
 * Features:
 * - URL synchronization (reads/writes ?tab= param)
 * - Visited tabs tracking for lazy loading
 * - Tab validation against allowed list
 * - Automatic default tab fallback
 *
 * @example
 * ```tsx
 * const { activeTab, setActiveTab, visitedTabs, isTabVisited } = useTabs({
 *   allowedTabs: ["overview", "contacts", "projects"],
 *   defaultTab: "overview",
 * });
 *
 * // Check if projects tab should be enabled
 * const projectsEnabled = isTabVisited("projects");
 * ```
 */
export function useTabs(options: UseTabsOptions = {}): UseTabsReturn {
  const router = useNavigationWithProgress();
  const searchParams = useSearchParams();

  const {
    allowedTabs = ["overview", "contacts", "jobsites", "projects"],
    defaultTab = "overview",
    syncWithUrl = true,
  } = options;

  // Initialize active tab from URL or default
  const [activeTab, setActiveTabState] = useState<string>(() => {
    if (syncWithUrl) {
      const tabParam = searchParams.get("tab");
      if (tabParam && allowedTabs.includes(tabParam)) {
        return tabParam;
      }
    }
    return defaultTab;
  });

  // Track visited tabs
  const [visitedTabs, setVisitedTabs] = useState<string[]>([defaultTab]);

  // Sync with URL search params
  useEffect(() => {
    if (!syncWithUrl) return;

    const tabParam = searchParams.get("tab");
    if (tabParam && allowedTabs.includes(tabParam) && tabParam !== activeTab) {
      setActiveTabState(tabParam);
    }
  }, [searchParams, allowedTabs, syncWithUrl, activeTab]);

  // Validate active tab against allowed tabs
  useEffect(() => {
    if (!allowedTabs.includes(activeTab)) {
      setActiveTabState(defaultTab);
    }
  }, [activeTab, allowedTabs, defaultTab]);

  // Track visited tabs
  useEffect(() => {
    setVisitedTabs((prev) =>
      prev.includes(activeTab) ? prev : [...prev, activeTab],
    );
  }, [activeTab]);

  // Set active tab with URL sync
  const setActiveTab = (tab: string) => {
    if (!allowedTabs.includes(tab)) {
      // Tab not in allowed tabs, skip update
      return;
    }

    setActiveTabState(tab);

    if (syncWithUrl) {
      const params = new URLSearchParams(searchParams.toString());
      if (tab === defaultTab) {
        params.delete("tab");
      } else {
        params.set("tab", tab);
      }

      const newUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;

      router.replace(newUrl, { scroll: false });
    }
  };

  // Check if tab is visited
  const isTabVisited = (tab: string) => visitedTabs.includes(tab);

  // Mark tab as visited
  const markTabVisited = (tab: string) => {
    setVisitedTabs((prev) => (prev.includes(tab) ? prev : [...prev, tab]));
  };

  // Reset visited tabs
  const resetVisitedTabs = () => {
    setVisitedTabs([defaultTab]);
  };

  return {
    activeTab,
    setActiveTab,
    visitedTabs,
    isTabVisited,
    markTabVisited,
    resetVisitedTabs,
  };
}

// Backward compatibility aliases for client-specific usage
export type UseClientTabsOptions = UseTabsOptions;
export type UseClientTabsReturn = UseTabsReturn;
export const useClientTabs = useTabs;
